import { Injectable } from '@angular/core';
import { createClient, Session, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '../../environments/environment';

export type SubscriptionStatus =
  | 'none'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'unpaid'
  | 'canceled';

export interface CompanySubscription {
  company_id: string;
  status: SubscriptionStatus;
  current_period_end: string | null;
  stripe_customer_id: string | null;
}

export interface CompanyMembership {
  company_id: string;
  company_name: string;
  role: 'admin' | 'member';
}

/**
 * Table/column names below (`company_members`, `companies`, `company_subscriptions`) are
 * placeholders inferred from the DNA-512 epic — DNA-513 (schema) hasn't landed yet. Update
 * once the real migration exists.
 */
@Injectable({
  providedIn: 'root',
})
export class Supabase {
  private _client?: SupabaseClient;

  /** Lazy so injecting this service doesn't crash when env vars are unset (e.g. in unit tests). */
  get client(): SupabaseClient {
    if (!this._client) {
      if (!environment.supabaseUrl || !environment.supabaseKey) {
        throw new Error('Supabase is not configured — check src/environments/environment*.ts');
      }
      // Explicit, defensive auth config: persist the session in localStorage and
      // silently refresh it so a page reload or navigation never drops the user
      // back to the login screen. detectSessionInUrl stays on for the magic-link
      // callback but is harmless on normal loads.
      this._client = createClient(environment.supabaseUrl, environment.supabaseKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          // Keep the default storage key + flow so existing sessions and the
          // magic-link callback keep working — no forced re-login on deploy.
        },
      });
    }
    return this._client;
  }

  getSession(): Promise<Session | null> {
    return this.client.auth.getSession().then(({ data }) => data.session);
  }

  onAuthStateChange(callback: (session: Session | null) => void) {
    return this.client.auth.onAuthStateChange((_event, session) => callback(session));
  }

  /** Sends a magic link. emailRedirectTo must be this web app's own /auth/callback — never the native app scheme. */
  signInWithMagicLink(email: string) {
    return this.client.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
  }

  /** Optional password path — same convention as the mobile app's web build. */
  signInWithPassword(email: string, password: string) {
    return this.client.auth.signInWithPassword({ email, password });
  }

  signOut() {
    return this.client.auth.signOut();
  }

  /** The current user's display name from auth metadata, or the email prefix. */
  async getDisplayName(): Promise<string> {
    const session = await this.getSession();
    const meta = (session?.user.user_metadata ?? {}) as Record<string, unknown>;
    return (
      (typeof meta['full_name'] === 'string' && meta['full_name']) ||
      (typeof meta['name'] === 'string' && meta['name']) ||
      session?.user.email?.split('@')[0] ||
      ''
    );
  }

  async updateDisplayName(name: string): Promise<void> {
    const { error } = await this.client.auth.updateUser({ data: { full_name: name.trim() } });
    if (error) throw new Error(error.message);
  }

  /** The user's account persona from profiles.role: 'brand' (buyer) or 'supplier'. Null if unset. */
  async getProfileRole(): Promise<string | null> {
    const session = await this.getSession();
    if (!session) return null;
    const { data, error } = await this.client
      .from('profiles')
      .select('role')
      .eq('id', session.user.id)
      .maybeSingle();
    if (error || !data) return null;
    return (data as { role: string | null }).role;
  }

  /** Company + role for the current user. Null when onboarding is incomplete (no company yet). */
  async getMyCompany(): Promise<CompanyMembership | null> {
    const { data, error } = await this.client
      .from('company_members')
      .select('company_id, role, companies(name)')
      .single();
    if (error || !data) return null;
    return {
      company_id: data['company_id'],
      company_name: (data as any).companies?.name ?? '',
      role: data['role'],
    };
  }

  /** RLS: members can read, only webhook/service role writes (see DNA-513). */
  async getCompanySubscription(companyId: string): Promise<CompanySubscription | null> {
    const { data, error } = await this.client
      .from('company_subscriptions')
      .select('company_id, status, current_period_end, stripe_customer_id')
      .eq('company_id', companyId)
      .maybeSingle();
    if (error || !data) return null;
    return data as CompanySubscription;
  }

  /** DNA-514: redirects the caller to Stripe Checkout. Throws with a user-facing message on failure. */
  async createCheckoutSession(companyId: string): Promise<string> {
    const { data, error } = await this.client.functions.invoke<{ url?: string; error?: string }>(
      'create-checkout-session',
      { body: { company_id: companyId } },
    );
    if (error || !data?.url) {
      throw new Error(data?.error ?? error?.message ?? 'Could not start checkout.');
    }
    return data.url;
  }

  /** DNA-515: redirects the caller to the Stripe Customer Portal. Throws with a user-facing message on failure. */
  async createPortalSession(companyId: string): Promise<string> {
    const { data, error } = await this.client.functions.invoke<{ url?: string; error?: string }>(
      'create-portal-session',
      { body: { company_id: companyId } },
    );
    if (error || !data?.url) {
      throw new Error(data?.error ?? error?.message ?? 'Could not open billing management.');
    }
    return data.url;
  }
}
